import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Icon } from "../";
import { useTranslation } from "react-i18next";
import {
  useDriverGroups,
  useUpdateDriverGroup,
  useDriverManagerDetail,
} from "../../../hooks";

const DriverGroupTab = ({ userId }) => {
  const { t } = useTranslation("agentTranslations");
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: driverGroupsData, isLoading } = useDriverGroups(searchTerm);
  const { data: userData } = useDriverManagerDetail(userId);
  const updateDriverGroupMutation = useUpdateDriverGroup();

  const driverGroups = driverGroupsData?.driverGroups || [];
  const currentUser = userData;

  const handleSearchChange = (term) => {
    setSearchInput(term);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  // Get current user's driver group IDs from administrators array
  const getUserDriverGroupIds = () => {
    if (!currentUser || !driverGroups.length) return [];

    const userGroups = [];
    driverGroups.forEach((group) => {
      const isAdmin = group.administrators?.some(
        (admin) => admin.id === currentUser.id,
      );
      if (isAdmin) {
        userGroups.push(group.id);
      }
    });
    return userGroups;
  };

  const [selectedGroups, setSelectedGroups] = useState([]);

  useEffect(() => {
    if (currentUser && driverGroups.length) {
      const userGroups = getUserDriverGroupIds();
      setSelectedGroups(userGroups);
    }
  }, [currentUser, driverGroups]);

  const handleToggleGroup = async (groupId) => {
    const isSelected = selectedGroups.includes(groupId);
    const previousSelectedGroups = [...selectedGroups];
    let newSelectedGroups;

    if (isSelected) {
      // Remove from selection
      newSelectedGroups = selectedGroups.filter((id) => id !== groupId);
    } else {
      // Add to selection
      newSelectedGroups = [...selectedGroups, groupId];
    }

    // Optimistically update UI
    setSelectedGroups(newSelectedGroups);

    try {
      // Get current administrators for this group (excluding current user)
      const currentGroup = driverGroups.find((g) => g.id === groupId);
      const currentAdminIds =
        currentGroup?.administrators
          ?.filter((admin) => admin.id !== userId)
          .map((admin) => admin.id) || [];

      // Combine with new selection
      const allAdminIds = isSelected
        ? currentAdminIds // Remove current user
        : [...currentAdminIds, userId]; // Add current user

      // Prepare administrators array as JSON string (format: "[72]" or "[72, 73]")
      const administrators =
        allAdminIds.length > 0 ? JSON.stringify(allAdminIds) : null;

      // Update the driver group
      await updateDriverGroupMutation.mutateAsync({
        driverGroupId: groupId,
        data: {
          administrators: administrators,
        },
      });

      // Refetch driver groups to get updated administrator lists
      await queryClient.invalidateQueries({ queryKey: ["driverGroups"] });
    } catch (error) {
      // Revert on error
      setSelectedGroups(previousSelectedGroups);
      console.error("Failed to update driver group:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  if (driverGroups.length === 0 && !searchTerm) {
    return (
      <div className="py-12 text-center">
        <Icon
          name="Users"
          className="text-muted-foreground mx-auto mb-4 h-12 w-12"
        />
        <h3 className="text-foreground mb-2 text-sm font-medium">
          No Driver Groups Available
        </h3>
        <p className="text-muted-foreground text-sm">
          There are no driver groups available to assign.
        </p>
      </div>
    );
  }

  const getTypeLabel = (type) => {
    if (type === 0) return "In-house drivers";
    if (type === 1) return "delivery companies";
    return "Unknown";
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="border-border bg-card mb-4 rounded-md border p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="Search" className="text-muted-foreground h-5 w-5" />
              </div>
              <input
                type="text"
                placeholder="Search driver groups..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-ring h-10 w-full rounded-sm border px-4 py-2 pr-10 pl-10 focus:ring-2 focus:outline-none"
              />
              {searchInput && (
                <Button
                  onClick={handleClearSearch}
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("actions.clearSearch")}
                  className="text-muted-foreground hover:bg-accent hover:text-foreground absolute top-1/2 right-1 min-h-8 min-w-8 -translate-y-1/2 rounded-md"
                >
                  <Icon name="X" className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {driverGroups.length === 0 && searchTerm ? (
        <div className="py-12 text-center">
          <Icon
            name="Search"
            className="text-muted-foreground mx-auto mb-4 h-12 w-12"
          />
          <h3 className="text-foreground mb-2 text-sm font-medium">
            No Driver Groups Found
          </h3>
          <p className="text-muted-foreground text-sm">
            No driver groups match your search criteria.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {driverGroups.map((group) => {
            const isSelected = selectedGroups.includes(group.id);
            const typeLabel = getTypeLabel(group.type);

            return (
              <div
                key={group.id}
                className={`bg-card rounded-md border p-4 transition-colors ${
                  isSelected
                    ? "border-primary bg-accent"
                    : "border-border hover:border-primary/60"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleGroup(group.id)}
                        disabled={updateDriverGroupMutation.isPending}
                        className="border-input bg-background text-primary focus:ring-ring h-4 w-4 cursor-pointer rounded-sm focus:ring-2"
                      />
                      <h3 className="text-foreground text-lg font-medium">
                        {group.name}
                      </h3>
                    </div>

                    <div className="ml-7">
                      <div className="text-muted-foreground flex items-center space-x-2 text-sm">
                        <Icon name="Tag" className="h-4 w-4" />
                        <span>Type: {typeLabel}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DriverGroupTab;
